import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { bornesDe, horodatageFiscal, isoDe, jourYmd, libellePeriode, useAncreDuJour, useHorlogeMinute, ymdDe, type PeriodeId } from '../periode';
import { formatMoney, formatMinor } from '@orgazone/money-format';
import { isInvoiceDocument, isRefundPiece } from '@orgazone/core-nf525';
import { Badge, Copyable, DataTable, DateRangePicker, EmptyState, FilterToolbar, Icon, Modal, Picker, rangeLabel, showToast, Tooltip, Trans, type ColumnDef, type DateRange, type DateRangeShortcut, type SortState, type TableStatus } from '@orgazone/ui-web';
import type { ColumnLayout, IconName } from '@orgazone/ui-tokens';
import {
  api, isSalesSortKey, type AbandonedDetailView, type AbandonedPageView, type AbandonedRow, type PendingSale, type PendingSalesView,
  type RegisterGroup, type SalesDetail, type SalesPageView, type SalesRow,
} from '../api';
import { useStoreScope } from '../store-context';
import { REGLEMENTS, libelleReglement } from '../reglements';
import { codeCaisse } from '../caisse';
import { key, t, type Translatable } from '../i18n';

/**
 * JOURNAL DES VENTES — ce qui s'est vendu dans le magasin ouvert, en lecture stricte.
 *
 * UTILITÉ : deuxième tranche verticale du back-office, et l'écran qu'un commerçant ouvre tous les
 * jours. Tout ce qui s'affiche sort de `fiscal_piece` de CE magasin, tel que la caisse l'a SCELLÉ.
 *
 * ⚠️ C'EST LE SEUL ÉCRAN OÙ RIEN NE PEUT SE MODIFIER, et ce n'est pas un choix d'interface : le
 * back-office ne signe jamais une pièce fiscale. Pas d'édition, pas de suppression, pas de « créer un
 * avoir » — un avoir est une pièce, seule une caisse peut le sceller. L'écran le DIT, au lieu d'offrir
 * un bouton que la loi interdit.
 *
 * ⚠️ CE QUI N'EST PAS ICI, ET POURQUOI : ni « réimprimer un duplicata » ni « exporter la pièce » (aucun
 * mécanisme côté back-office : le duplicata est signé par la caisse, sur son imprimante), ni lien vers
 * la fiche client (l'écran Clients n'existe pas). Ce qui n'est pas branché s'enlève, il ne se simule
 * pas (§23.0). Le journal des duplicata DÉJÀ tirés, lui, est réel : il se lit dans le dépliage.
 *
 * ⚠️ LES BORNES DE PÉRIODE SONT CALCULÉES ICI, dans le fuseau de celui qui regarde, et envoyées en
 * AAAAMMJJ. `datetime_fiscal` est l'heure locale du magasin ; laisser le serveur (UTC) décider de
 * « aujourd'hui » ferait commencer la journée du commerçant à 2 h du matin, sans jamais le dire.
 */
export function Journal() {
  const scope = useStoreScope();
  const gln = scope.view?.active?.gln ?? null;

  const [tab, setTab] = useState<Onglet>('all');
  // ⚠️ LE CRAN N'EST PLUS UN ÉTAT : depuis que le sélecteur est le seul contrôle de période, tout
  //    choix produit une PLAGE. Le cran ne subsiste que comme point de départ à l'ouverture — le garder
  //    en état laisserait croire à une seconde source de bornes, qui ne changerait jamais.
  const period: PeriodeId = PERIODE_OUVERTURE;
  // ⚠️ LA PLAGE LIBRE PREND LE PAS SUR LE CRAN, elle ne le désigne pas : deux sources de bornes qui
  //    se contrediraient feraient afficher un libellé de période qui n'est pas celui des chiffres.
  const [plage, setPlage] = useState<DateRange | null>(null);
  // ⚠️ PLUSIEURS CAISSES, comme la maquette : on regarde « les deux caisses du rez-de-chaussée », pas
  //    une seule. Le mono-choix obligeait à relire la période caisse par caisse.
  const [registers, setRegisters] = useState<string[]>([]);
  /**
   * LES GROUPES DE CAISSES — la promesse du dessin : « les caisses du 2ᵉ étage » en un clic.
   *
   * ⚠️ CE N'EST PAS UN FILTRE DE PLUS, c'est un RACCOURCI vers celui des caisses. Un second filtre
   * envoyé au serveur ferait deux vérités à tenir d'accord (« groupe A » ET « caisses R01, R05 »)
   * et poserait la question de ce qui gagne quand ils se contredisent. Ici le groupe REMPLIT la
   * sélection existante : ce qu'on voit filtrer est exactement ce qui filtre.
   * ⚠️ `null` = groupes non lus. Le bloc disparaît alors — proposer un raccourci qui n'aboutit pas
   *    serait pire que son absence.
   */
  const [groupes, setGroupes] = useState<RegisterGroup[] | null>(null);
  /** Y a-t-il des groupes à proposer ? Décide AUSSI de la forme du filtre des caisses (voir plus bas). */
  const avecGroupes = groupes !== null && groupes.length > 0;
  useEffect(() => {
    // ⚠️ On réutilise la lecture de topologie plutôt que d'ajouter une route : elle est légère, déjà
    //    gardée, et une seconde porte sur la même donnée finirait par en dire autre chose.
    let vivant = true;
    api.storeTopology().then((topologie) => { if (vivant) setGroupes(topologie.groups); }).catch(() => { if (vivant) setGroupes(null); });
    return () => { vivant = false; };
  }, []);
  /**
   * La sélection de caisses réduite à une CLÉ STABLE.
   * ⚠️ Un tableau est recréé à chaque rendu : le déclarer tel quel en dépendance relancerait la
   * requête en boucle. L’auteur l’avait vu et joignait la chaîne DANS le tableau de dépendances — ce
   * qu’eslint refuse (expression complexe), et la CI échoue à `--max-warnings 0`. Extraite ici, la
   * clé est statiquement vérifiable ET l’intention est préservée.
   */
  const caissesCle = registers.join(',');
  const [vendeur, setVendeur] = useState('');
  const [reglement, setReglement] = useState('');
  const [q, setQ] = useState('');
  const [applied, setApplied] = useState('');
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
  const [pageIndex, setPageIndex] = useState(0);
  const [layout, setLayout] = useState<ColumnLayout>(DEFAULT_LAYOUT);
  const [data, setData] = useState<SalesPageView | null>(null);
  const [status, setStatus] = useState<TableStatus>({ phase: 'idle' });
  const [attempt, setAttempt] = useState(0);
  // ⚠️ UNE MODALE, PAS UN DÉPLIAGE (décision utilisateur 2026-08-19, conforme à la maquette) : le
  //    détail d'une pièce porte des mentions fiscales — chaîne de signature, ventilation figée à la
  //    vente, interdiction d'avoir depuis le back-office — qui se lisent mal coincées entre deux
  //    lignes de liste. La modale leur donne la place, et la liste garde son rythme.
  const [pieceOuverte, setPieceOuverte] = useState<SalesRow | null>(null);
  /** La période des CHIFFRES actuellement rendus — pas celle du segment cliqué à l'instant. */
  /** Le libellé de la période RÉELLEMENT rendue — il suit les chiffres, jamais l'intention. */
  const [periodeRendue, setPeriodeRendue] = useState<string>(libellePeriode(PERIODE_OUVERTURE));

  // `?open=<id de pièce>` : la composition de stock d'une fiche article renvoie ici pour LIRE la vente
  // derrière un mouvement. Le détail porte la pièce entière (`detail.piece`) : la modale s'ouvre sans
  // dépendre de la page ni de la période affichée. Un identifiant inconnu se DIT (§23.0).
  useEffect(() => {
    const target = new URLSearchParams(window.location.search).get('open');
    if (!target) return;
    let vivant = true;
    api.salesPiece(target)
      .then((d) => { if (vivant) setPieceOuverte(d.piece); })
      .catch((e: unknown) => { if (vivant) showToast(t('Pièce introuvable : {v}', { v: e instanceof Error ? e.message : String(e) }), 'err'); });
    return () => { vivant = false; };
  }, []);
  // ⚠️ CHARGÉS EN PERMANENCE, pas seulement sur leur onglet : c'est leur COMPTE qui doit alerter
  //    depuis les autres onglets. Il n'y en a que des unités — la lecture ne coûte rien.
  const [waiting, setAttente] = useState<PendingSalesView | null>(null);
  const [attenteErr, setAttenteErr] = useState<string | null>(null);
  /** Des lignes sont-elles RÉELLEMENT à l'écran ? (distingue premier chargement et rechargement). */
  const displays = useRef(false);

  // Le champ se calme avant de partir au serveur ; la page revient à 1 dès que le terme change.
  useEffect(() => {
    const timer = setTimeout(() => { setApplied(q.trim()); setPageIndex(0); }, 300);
    return () => clearTimeout(timer);
  }, [q]);

  /**
   * ⚠️ « AUJOURD'HUI » DOIT RESTER AUJOURD'HUI. Les bornes étaient calculées une fois par choix de
   * période : un onglet laissé ouvert dans le magasin la nuit continuait d'interroger la journée de
   * la VEILLE sous le libellé « Aujourd'hui » — et recliquer le segment déjà actif ne corrigeait
   * rien (même valeur, aucune invalidation). L'ancre bascule d'elle-même au changement de jour, et
   * la requête repart puisque les bornes sont ses dépendances.
   */
  const ancre = useAncreDuJour();
  const bornes = useMemo(
    () => (plage ? { from: ymdDe(plage.from), to: ymdDe(plage.to) } : bornesDe(period, ancre)),
    [plage, period, ancre],
  );
  /**
   * Les périodes proposées d'un clic — celles de la maquette (§25, `jDateShortcuts`).
   * ⚠️ CALCULÉES ICI, jamais dans le composant : le sélecteur ne connaît ni le fuseau du magasin ni
   * ce qu'« hier » veut dire un lundi matin. Il présente des bornes, il n'en fabrique aucune.
   */
  const raccourcis = useMemo<DateRangeShortcut[]>(() => {
    const out = RACCOURCIS.flatMap((id) => {
      const b = bornesDe(id, ancre);
      return b.from && b.to ? [{ id, label: libellePeriode(id), from: isoDe(b.from), to: isoDe(b.to) }] : [];
    });
    // ⚠️ « TOUT L'HISTORIQUE » EST BORNÉ PAR LA DONNÉE, comme la maquette (`ZMIN`) : sa borne basse
    //    est le jour de la PLUS ANCIENNE pièce du magasin. Sans elle, il faudrait inventer une date
    //    — une plage affichée que rien ne fonde (§23.0). Absent tant que la réponse n'est pas là :
    //    le raccourci n'apparaît qu'une fois qu'on sait jusqu'où remonter.
    const debut = data?.firstDay;
    if (debut) {
      out.push({
        id: 'all',
        label: libellePeriode('all'),
        from: `${debut.slice(0, 4)}-${debut.slice(4, 6)}-${debut.slice(6, 8)}`,
        to: isoDe(jourYmd(ancre)),
      });
    }
    return out;
  }, [ancre, data?.firstDay]);
  /** Ce que l'écran annonce comme période lue — la plage posée, ou le cran par défaut. */
  const periodHeading = plage ? rangeLabel(plage, raccourcis, '') : libellePeriode(period);

  useEffect(() => {
    // ⚠️ AUCUNE REQUÊTE SANS MAGASIN : `idle` dit « rien n'est parti », ce qui n'est pas « chargement ».
    if (!gln) { setStatus({ phase: 'idle' }); setData(null); return; }
    let vivant = true;
    setStatus(displays.current ? { phase: 'reloading' } : { phase: 'loading' });
    api.sales({
      // ⚠️ UNE CLÉ QUE LE SERVEUR NE CONNAÎT PAS RETOMBE SUR LE TRI PAR DÉFAUT, jamais un cast : un journal trié autrement
      //    qu'on ne l'a demandé se voit, un journal qui refuse de se charger arrête le travail.
      page: pageIndex, size: PAGE_SIZE, sort: isSalesSortKey(sort.key) ? sort.key : 'heure', dir: sort.dir,
      // ⚠️ « en attente » et « abandonnées » ne sont pas des onglets du JOURNAL : la requête fiscale continue sur
      //    « toutes » (elle alimente le compteur des annulations, visible depuis n'importe quel onglet).
      tab: tab === 'pending' || tab === 'abandoned' ? 'all' : tab,
      from: bornes.from, to: bornes.to, register: caissesCle, q: applied,
      operator: vendeur, method: reglement,
    })
      .then((page) => {
        if (!vivant) return;
        displays.current = true;
        setData(page);
        // ⚠️ Le libellé de période suit les CHIFFRES, pas l'intention : pendant un rechargement, la
        // somme d'une période était annoncée sous le nom de la suivante.
        setPeriodeRendue(periodHeading);
        setStatus({ phase: 'ready' });
      })
      .catch((e: unknown) => {
        if (!vivant) return;
        // ⚠️ CANAL ALERTE, message RÉEL, et AUCUNE ligne rendue : un journal vide ferait passer une
        // panne pour une journée sans vente — sur un écran adossé au fiscal, c'est un risque réel.
        displays.current = false;
        setData(null);
        setStatus({
          phase: 'error',
          message: e instanceof Error ? e.message : String(e),
          detail: t('Rien n’est affiché tant que la lecture n’a pas abouti : ces pièces viennent de la base de ce magasin, et elles n’ont pas pu être lues.'),
          onRetry: () => setAttempt((n) => n + 1),
        });
      });
    return () => { vivant = false; };
    // `periode` est déclarée bien qu'elle ne pilote pas la requête : elle voyage AVEC la réponse
    // (le libellé du pied suit les chiffres rendus, pas le segment cliqué à l'instant).
  }, [gln, pageIndex, sort.key, sort.dir, tab, bornes.from, bornes.to, caissesCle, vendeur, reglement, applied, attempt, periodHeading]);

  useEffect(() => {
    if (!gln) { setAttente(null); setAttenteErr(null); return; }
    let vivant = true;
    api.salesPending()
      .then((v) => { if (vivant) { setAttente(v); setAttenteErr(null); } })
      // ⚠️ L'ÉCHEC SE DIT, il ne se rend pas par « aucun panier » : un panier suspendu invisible est
      //    de l'argent qu'on croit encaissé.
      .catch((e: unknown) => { if (vivant) { setAttente(null); setAttenteErr(e instanceof Error ? e.message : String(e)); } });
    return () => { vivant = false; };
  }, [gln, attempt]);

  /**
   * LES VENTES ABANDONNÉES, LUES EN PERMANENCE comme les paniers en attente : leur COMPTE doit alerter depuis les autres
   * onglets. Même période, mêmes caisses, même vendeur que le journal ; le filtre « paiement engagé » est le leur.
   * ⚠️ UN ÉCHEC SE DIT : « aucune vente abandonnée » est une bonne nouvelle, on ne l'annonce pas sur une panne.
   */
  const [paymentOnly, setPaymentOnly] = useState(false);
  const [abPageIndex, setAbPageIndex] = useState(0);
  const [abandons, setAbandons] = useState<AbandonedPageView | null>(null);
  const [abStatus, setAbStatus] = useState<TableStatus>({ phase: 'idle' });
  const [abandonOuvert, setAbandonOuvert] = useState<AbandonedRow | null>(null);
  useEffect(() => {
    // ⚠️ SANS BORNES, RIEN NE PART : la lecture exige une période (« tout l'historique » relirait le magasin entier).
    if (!gln || !bornes.from || !bornes.to) { setAbandons(null); setAbStatus({ phase: 'idle' }); return; }
    let vivant = true;
    setAbStatus((st) => (st.phase === 'ready' || st.phase === 'reloading' ? { phase: 'reloading' } : { phase: 'loading' }));
    api.salesAbandoned({
      from: bornes.from, to: bornes.to, register: caissesCle, operator: vendeur, payment: paymentOnly, page: abPageIndex, size: PAGE_SIZE,
    })
      .then((v) => { if (vivant) { setAbandons(v); setAbStatus({ phase: 'ready' }); } })
      .catch((e: unknown) => {
        if (!vivant) return;
        setAbandons(null);
        setAbStatus({
          phase: 'error',
          message: e instanceof Error ? e.message : String(e),
          detail: t('Rien n’est affiché tant que la lecture n’a pas abouti : « aucune vente abandonnée » ne s’annonce pas sur une panne.'),
          onRetry: () => setAttempt((n) => n + 1),
        });
      });
    return () => { vivant = false; };
  }, [gln, bornes.from, bornes.to, caissesCle, vendeur, paymentOnly, abPageIndex, attempt]);

  const onSortChange = useCallback((next: SortState) => { setSort(next); setPageIndex(0); }, []);
  const changerOnglet = useCallback((next: Onglet) => { setTab(next); setPageIndex(0); setPieceOuverte(null); }, []);

  const columns = useMemo(() => buildColumns(), []);
  const rows = data?.rows ?? [];
  const summary = data?.summary ?? null;
  // ⚠️ CE QUE LA PÉRIODE OFFRE, à ne pas confondre avec ce qui est CHOISI (`caisses`, `vendeur`…) :
  //    un filtre ne propose que ce qui a réellement écrit, sinon on cherche là où il n'y a rien.
  const caissesDispo = data?.registers ?? [];
  const vendeursDispo = data?.operators ?? [];
  const reglementsDispo = data?.methods ?? [];

  // ⚠️ QUATRE SITUATIONS SANS TABLEAU, jamais confondues : contexte en cours de lecture · contexte
  // ILLISIBLE (une panne) · aucun magasin ouvert au compte · aucun magasin choisi.
  if (!gln) {
    return (
      <>
        <Heading />
        <div className="dcard" style={{ padding: 0, overflow: 'hidden' }}>
          {scope.loading && (
            <EmptyState icon="store" title={t('Lecture du contexte…')} note={t('Le back-office cherche quel magasin cette session administre.')} />
          )}
          {!scope.loading && scope.error && (
            <div role="alert" style={{ padding: 24 }}>
              <div style={{ fontSize: 'var(--t-h4)', fontWeight: 600, color: 'var(--danger-ink)', marginBottom: 6 }}>
                
                {t('Contexte magasin illisible')}
              </div>
              <p style={{ margin: 0, color: 'var(--ink-500)', fontSize: 'var(--t-body)', lineHeight: 1.6 }}>
                {t('Le magasin de cette session n’a pas pu être lu : {error}. Aucune vente n’est affichée — ce n’est pas un journal vide, c’est une lecture qui n’a pas abouti.', { error: scope.error })}</p>
            </div>
          )}
          {!scope.loading && !scope.error && (
            <EmptyState
              icon="store"
              title={scope.view && scope.view.stores.length === 0 ? t('Aucun magasin ouvert à ce compte') : t('Aucun magasin actif')}
              note={
                scope.view && scope.view.stores.length === 0
                  ? t('Aucun magasin n’est ouvert à ce compte : un administrateur doit accorder l’accès magasin par magasin. Sans magasin, il n’y a aucun journal à lire.')
                  : t('Choisissez le magasin à administrer dans la barre du haut. Le journal est celui de CE magasin — aucun écran n’en affiche plusieurs à la fois.')
              }
            />
          )}
        </div>
      </>
    );
  }

  /** Un filtre commun ramène les DEUX tableaux à leur première page. */
  const premierePage = () => { setPageIndex(0); setAbPageIndex(0); };
  // ⚠️ LES FILTRES COMMUNS AUX DEUX TABLEAUX DE L'ÉCRAN (le journal, les ventes abandonnées) : même période, mêmes
  //    caisses, même vendeur — passer d'un onglet à l'autre ne change pas ce qu'on regarde. Écrits une fois : deux
  //    copies du même contrôle finiraient par proposer deux choses.
  const filtreGroupes = (<>
    {avecGroupes && (
      <GroupesDeCaisses
        groupes={groupes!} registers={registers}
        onAppliquer={(ids) => { setRegisters(ids); premierePage(); }}
      />
    )}
  </>);
  const filtreCaisses = (caissesDispo: string[]) => (<>
    {/* ⚠️ À LA QUATRIÈME CAISSE, LE FILTRE PASSE EN LISTE : le composant partagé porte la
        bascule, l'écran ne la connaît pas — il ne fixe que le seuil.
        ⚠️ ET DÈS QU'UN GROUPE EXISTE, LE SEUIL TOMBE À ZÉRO (retour utilisateur 2026-09-07 :
        « un bouton de plus est très confusant »). Motif : les groupes se rendent en
        PASTILLES, et les caisses aussi sous le seuil — deux familles qui se ressemblent
        côte à côte, alors qu'un clic sur l'une remplit ce que l'autre affiche. Replier les
        caisses en liste laisse UNE seule famille de pastilles dans la rangée, celle qui
        porte le geste nouveau. ⚠️ Sans groupe, rien ne change : sur trois caisses, un
        panneau qu'il faut ouvrir cacherait un choix qu'on voyait d'un coup. */}
    <Picker
      id="bo-journal-caisses"
      label={t('Caisses')}
      allLabel={t('Toutes')}
      multiple
      chipsMax={avecGroupes ? 0 : 3}
      value={registers}
      onChange={(next) => { setRegisters(next); premierePage(); }}
      // ⚠️ UNE CAISSE CHOISIE RESTE OFFERTE même absente de la période : sinon le contrôle
      //    annonce « toutes » pendant qu'il filtre, et on ne peut plus le défaire depuis lui.
      options={[...new Set([...caissesDispo, ...registers])].map((c) => ({ value: c, label: codeCaisse(c) }))}
    />
  </>);
  const filtrePeriode = (<>
    {/* ⚠️ UN SEUL CONTRÔLE DE PÉRIODE, comme la maquette : ses raccourcis SONT les crans, et
        les champs Du/Au prennent le relais quand aucun ne convient. Deux contrôles côte à côte
        obligeraient à deviner lequel pilote l'écran. */}
    <DateRangePicker
      value={plage}
      onChange={(v) => { setPlage(v); premierePage(); }}
      shortcuts={raccourcis}
      // ⚠️ La borne haute est AUJOURD'HUI : demander demain rendrait « aucune pièce », et cette
      //    réponse-là se confond avec une journée sans vente.
      max={isoDe(jourYmd(ancre))}
      // ⚠️ LE DÉCLENCHEUR NOMME LA PÉRIODE ACTIVE, y compris quand aucune plage n'est posée :
      //    sans `placeholder`, il annonçait « Dates personnalisées » pendant que l'écran
      //    filtrait sur aujourd'hui — le contrôle qui gouverne la période ne la disait pas.
      placeholder={libellePeriode(period)}
      idleHint={t('Le journal affiche « {v} »', { v: libellePeriode(period) })}
      clearLabel={t('Revenir à la période par défaut')}
      label={t('Période du journal')}
    />
  </>);
  const filtreVendeur = (vendeursDispo: string[]) => (<>
    {/* ⚠️ LE VENDEUR PASSE PAR LE PICKER PARTAGÉ, comme la maquette (`pkJVend`) : un magasin
        peut compter trente vendeurs, et une liste native s'y parcourt à l'aveugle. Le picker
        porte la recherche.
        ⚠️ PAS DE CRÉATION ICI : on FILTRE un journal, on n'inscrit pas un vendeur. Offrir
        « créer » sur un filtre écrirait dans un référentiel depuis un écran de lecture. */}
    <Picker
      id="bo-journal-vendeur"
      label={t('Vendeur')}
      allLabel={t('Tous les vendeurs')}
      value={vendeur}
      onChange={(v) => { setVendeur(v); premierePage(); }}
      options={(vendeursDispo.includes(vendeur) || !vendeur ? vendeursDispo : [vendeur, ...vendeursDispo])
        .map((v) => ({ value: v, label: v }))}
    />
  </>);

  return (
    <>
      <Heading />
      <Onglets
        current={tab}
        onChange={changerOnglet}
        /* ⚠️ Le badge ambre du seul onglet des annulations vient de la SYNTHÈSE de la période, pas de la
           page : compter les annulations visibles ferait clignoter le badge au fil de la pagination. */
        /* ⚠️ LES COMPTES VIENNENT DE LA SYNTHÈSE DE PÉRIODE, jamais de la page : compter les lignes
           visibles ferait clignoter les onglets au fil de la pagination. Les paniers, eux, n'ont pas
           de période — ils sont ce qui attend, maintenant. */
        comptes={{
          all: summary?.pieces ?? null,
          cancelled: summary?.cancelledCount ?? null,
          credits: summary?.creditCount ?? null,
          pending: waiting?.rows?.length ?? null,
          // ⚠️ `?.` À CHAQUE NIVEAU : une réponse sans `summary` ne doit pas faire tomber l'écran entier pendant le rendu.
          abandoned: abandons?.summary?.count ?? null,
        }}
      />
      {/* Le panneau que les onglets pilotent — sans lui, `aria-controls` désigne le vide. */}
      <div role="tabpanel" id="bo-journal-panneau" aria-labelledby={`bo-journal-onglet-${tab}`}>
      {tab === 'pending' ? (
        <Attente vue={waiting} erreur={attenteErr} onRetry={() => setAttempt((n) => n + 1)} />
      ) : tab === 'abandoned' ? (
        <Abandons
          vue={abandons} status={abStatus} pageIndex={abPageIndex} onPageChange={setAbPageIndex} onOpen={setAbandonOuvert}
          toolbar={
            <FilterToolbar
              search={null}
              actifs={(registers.length > 0 ? 1 : 0) + (plage ? 1 : 0) + (vendeur ? 1 : 0) + (paymentOnly ? 1 : 0)}
              title={t('Filtres des ventes abandonnées')}
              filters={<>
                {filtreGroupes}
                {filtreCaisses(abandons?.registers ?? [])}
                {filtrePeriode}
                {filtreVendeur(abandons?.operators ?? [])}
                <PaiementEngage actif={paymentOnly} onChange={(v) => { setPaymentOnly(v); setAbPageIndex(0); }} />
              </>}
            />
          }
        />
      ) : (
      <DataTable<SalesRow>
        tableId="bo-journal"
        unit={{ one: t('pièce'), gender: 'f' }}
        columns={columns}
        rows={rows}
        getRowId={(r) => r.id}
        status={status}
        sort={sort}
        onSortChange={onSortChange}
        defaultSort={DEFAULT_SORT}
        // ⚠️ `data?.page?.index` ET NON `data?.page.index` — le chaînage s'arrêtait UN NIVEAU TROP TÔT
        //    (corrigé 2026-09-07). Une réponse sans `page` — un service qui change de forme, un
        //    rattrapage d'erreur qui rend un objet nu — ne donnait pas un tableau vide mais un ÉCRAN
        //    BLANC : `undefined.index` lève pendant le rendu, et React démonte tout l'arbre. Le
        //    journal est l'écran d'où l'on part quand quelque chose ne va pas ; il doit tomber en
        //    marche.
        page={{ index: data?.page?.index ?? pageIndex, size: data?.page?.size ?? PAGE_SIZE, total: data?.total ?? 'unknown' }}
        onPageChange={setPageIndex}
        columnLayout={layout}
        onColumnLayoutChange={(next) => setLayout(next)}
        reorderableColumns
        /* ⚠️ Une pièce annulée reste au journal, en teinte de mise à l'écart — elle n'est ni effacée ni
           silencieuse : c'est la trace de ce qui a failli se passer. */
        rowTone={(r) => (r.cancelledBy ? 'muted' : undefined)}
        /* ⚠️ LA LIGNE ENTIÈRE OUVRE LA PIÈCE — pas un chevron : sur un écran de lecture, tout ce que
           porte la ligne est une invitation à voir le détail. */
        onRowActivate={(row) => setPieceOuverte(row)}
        /* ⚠️ « Un total qui ne dit pas ce qu'il exclut est un total faux » : la ligne de total porte la
           somme RETENUE, et sa note énonce ce qui en sort. */
        totalRow={summary ? {
          cells: {
            ht: <Montant cents={summary.netExclTaxCents ?? null} barre={false} />,
            tva: <Montant cents={summary.netTaxCents ?? null} barre={false} />,
            total: <strong>{formatMoney(summary.netCents, { thousandsSep: ' ' })}</strong>,
          },
          note: <Arithmetique s={summary} period={periodeRendue} />,
        } : undefined}
        toolbar={
          /* ⚠️ L'ORDRE EST CELUI DE LA MAQUETTE (§25, `data-tour="j-filters"`) : recherche,
              caisses, période, vendeur, règlement. Il n'est pas arbitraire — on cherche d'abord une
              pièce, on restreint ensuite. Tous les contrôles sont à 40 px, comme elle : une hauteur
              différente dans une même ligne fait lire deux familles de contrôles.
              ⚠️ La « saison » de la maquette n'est PAS ici : aucune colonne de saison n'existe sur
              l'article repris. Offrir le filtre serait promettre un tri impossible (§23.0).
              ⚠️ SUR MOBILE, seule la RECHERCHE reste en barre — les filtres passent en tiroir
              (`FilterToolbar`, décision utilisateur 2026-08-24) avec le compte des actifs. */
          <FilterToolbar
            search={<Recherche value={q} onChange={setQ} />}
            actifs={(registers.length > 0 ? 1 : 0) + (plage ? 1 : 0) + (vendeur ? 1 : 0) + (reglement ? 1 : 0)}
            title={t('Filtres du journal')}
            filters={<>
            {filtreGroupes}
            {filtreCaisses(caissesDispo)}
            {filtrePeriode}
            {filtreVendeur(vendeursDispo)}
            {/* ⚠️ LE MÊME CONTRÔLE QUE LE VENDEUR, et c'est la raison qui prime : deux contrôles de
                même nature dans une même barre doivent se ressembler. La maquette y met une liste
                native, mais son picker n'était pas un composant partagé — le nôtre l'est, et une
                liste native à côté d'un picker se lit comme une autre famille de contrôle.
                ⚠️ Neuf entrées connues d'avance n'ont pas besoin de recherche ; elles ne justifient
                pas pour autant une SECONDE apparence. */}
            <Picker
              id="bo-journal-reglement"
              label={t('Règlement')}
              allLabel={t('Tous les règlements')}
              value={reglement}
              onChange={(v) => { setReglement(v); setPageIndex(0); }}
              options={(reglementsDispo.includes(reglement) || !reglement ? reglementsDispo : [reglement, ...reglementsDispo])
                .map((m) => ({ value: m, label: libelleReglement(m) }))}
            />
          </>}
          />
        }
        /* ⚠️ `filtersActive` DÉCLARÉ : la période est un filtre PERMANENT (« aujourd'hui » par défaut).
           Sans cette déclaration, un magasin sans vente du jour annoncerait « aucune pièce dans ce
           magasin » alors qu'il en a des milliers la veille.
           ⚠️ AUCUNE PUCE DE FILTRE APPLIQUÉ, comme la maquette : ici c'est le CONTRÔLE qui filtre et
           qui porte son état — le sélecteur dit sa période, les pastilles disent leurs caisses, les
           listes disent leur valeur. Une rangée de puces les répéterait sous eux.
           ⚠️ Ce n'est pas un renoncement à la règle « un filtre restauré SE VOIT » : cet écran ne
           PERSISTE aucun filtre (pas de `tableFiltersKey`), donc rien n'est jamais restauré à l'insu
           de qui regarde. Le jour où il en persistera, les puces devront revenir avec. */
        filtersActive
        emptyStates={{
          empty: {
            icon: 'receipt',
            title: t('Aucune pièce dans ce magasin'),
            note: t('La base de ce magasin ne contient aucune vente. Les pièces remontent des caisses à la synchronisation — rien ne se saisit depuis cet écran, qui est en lecture.'),
          },
          noMatch: {
            title: t('Aucune pièce sur cette période'),
            note: t('Élargissez la période ou effacez les filtres. Une journée sans vente est possible ; ce n’est pas une panne.'),
          },
        }}
      />
      )}
      </div>

      {/* ⚠️ LA MODALE EST MONTÉE HORS DU PANNEAU D'ONGLET : rendue dedans, elle serait annoncée comme
          un contenu de l'onglet courant, et changer d'onglet la ferait disparaître sans la fermer. */}
      <Modal
        open={pieceOuverte !== null}
        onClose={() => setPieceOuverte(null)}
        title={pieceOuverte?.pieceNumber || t('Pièce sans numéro')}
        subtitle={pieceOuverte ? horodatageFiscal(pieceOuverte.datetimeFiscal) : undefined}
        icon="receipt"
        width="lg"
        hero
        meta={pieceOuverte ? <MetaPiece row={pieceOuverte} /> : undefined}
      >
        {pieceOuverte && <Piece id={pieceOuverte.id} />}
      </Modal>
      <AbandonedSaleModal row={abandonOuvert} onClose={() => setAbandonOuvert(null)} />
    </>
  );
}

/**
 * LA FENÊTRE D'UN PANIER ABANDONNÉ — qui, où, pourquoi, et ce qu'il y avait au panier.
 * ⚠️ EXPORTÉE : le journal d'activité ouvre la même, depuis sa ligne d'abandon — deux fenêtres du même panier
 *    finiraient par dire deux choses.
 */
export function AbandonedSaleModal({ row, onClose }: { row: AbandonedRow | null; onClose: () => void }) {
  return (
    <Modal
      open={row !== null}
      onClose={onClose}
      title={t('Vente abandonnée')}
      subtitle={row ? horodatageFiscal(row.voidedAtLocal) : undefined}
      icon="receipt"
      width="lg"
      hero
      meta={row ? <MetaAbandon row={row} /> : undefined}
    >
      {row && <PanierAbandonne row={row} />}
    </Modal>
  );
}

/**
 * LA BANDE D'IDENTITÉ de la pièce ouverte — caisse, opérateur, nature, et son sort.
 * ⚠️ DANS L'EN-TÊTE FIXE : rendue dans le corps, elle défilerait et disparaîtrait au premier coup de
 * molette — exactement au moment où l'on cherche à savoir ce qu'on lit.
 */
function MetaPiece({ row }: { row: SalesRow }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: '12.5px', color: 'var(--ink-600)' }}>
      <span>{t('Caisse')} <strong style={{ color: 'var(--ink-800)' }}>{row.registerId || '—'}</strong></span>
      <span aria-hidden style={{ color: 'var(--ink-300)' }}>·</span>
      <span>{row.operatorName || t('Opérateur inconnu')}</span>
      <span aria-hidden style={{ color: 'var(--ink-300)' }}>·</span>
      <span>{row.clientName || t('Comptoir')}</span>
      <TypePiece row={row} />
      {/* ⚠️ L'ANNULATION SE DIT DANS L'EN-TÊTE, pas seulement dans le corps : c'est la première chose
          à savoir d'une pièce, et elle change la lecture de tous les montants en dessous. */}
      {row.cancelledBy && (
        <Badge tone="warn" size="xs">{t('Annulée par {cancelledBy}', { cancelledBy: row.cancelledBy })}</Badge>
      )}
    </div>
  );
}

/**
 * LES PÉRIODES PROPOSÉES D'UN CLIC dans le sélecteur — celles de la maquette (§25).
 * ⚠️ « Tout l'historique » n'y figure PAS : ce n'est pas une plage de dates, c'est l'absence de
 *    borne. Il vit sur la puce de filtre, qui sait dire « aucune période ».
 */
const RACCOURCIS: PeriodeId[] = ['today', 'yesterday', '7d', 'week', 'month', 'lastMonth', '14d', 'lastYear'];

/** La période affichée À L'OUVERTURE — un journal se lit d'abord au jour le jour. */
const PERIODE_OUVERTURE: PeriodeId = 'today';

/** 14 pièces par page — la valeur de la maquette, appliquée et plafonnée au serveur. */
const PAGE_SIZE = 14;

/** Le plus récent en tête : un journal se lit par le haut. */
const DEFAULT_SORT: SortState = { key: 'heure', dir: 'desc' };

/** Réglage initial des colonnes : l'ordre déclaré, rien de masqué. */
const DEFAULT_LAYOUT: ColumnLayout = { order: [], hidden: [] };

type Onglet = 'all' | 'cancelled' | 'credits' | 'pending' | 'abandoned';

/** En-tête de page : le titre, et la contrainte qui a dessiné l'écran. */
function Heading() {
  return (
    <header style={{ marginBottom: 18 }}>
      {/* ⚠️ TITRE ET SOUS-TITRE REPRIS MOT POUR MOT de la maquette (25 px / 13,5 px) : la phrase n'est
             pas décorative, elle annonce la CONTRAINTE de l'écran — et c'est elle qui explique
             l'absence de tout bouton d'édition plus bas. */}
      <h1 style={{ fontSize: '25px', fontWeight: 700, letterSpacing: '-0.025em', margin: 0, color: 'var(--ink-900)' }}>
        
        {t('Journal des ventes')}
      </h1>
      <p style={{ margin: '3px 0 0', fontSize: '13.5px', color: 'var(--ink-500)' }}>
        <Trans k='Toutes les pièces signées par les caisses. <1>Le seul écran du back-office où rien ne se modifie.</1>' parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} />
      </p>
    </header>
  );
}

/**
 * Les cinq onglets — cinq QUESTIONS, pas cinq filtres du même jeu.
 *
 * ⚠️ « VENTES EN ATTENTE » N'EST PAS UN FILTRE DU JOURNAL : il lit une AUTRE source (les paniers
 * suspendus sur les caisses), qui n'a ni numéro de pièce ni signature. Le fondre dans la liste
 * fiscale ferait passer un brouillon pour une vente.
 * ⚠️ « VENTES ABANDONNÉES » non plus : des paniers vidés avant d'être encaissés, une trace de la caisse, jamais une
 * pièce. Même période et mêmes filtres que le journal, pour qu'on les lise côte à côte.
 */
function Onglets({ current, onChange, comptes }: {
  current: Onglet; onChange: (o: Onglet) => void; comptes: Record<Onglet, number | null>;
}) {
  const items: { id: Onglet; label: string }[] = [
    { id: 'all', label: t('Toutes les pièces') },
    { id: 'cancelled', label: t('Ventes annulées') },
    { id: 'credits', label: t('Avoirs & retours') },
    { id: 'pending', label: t('Ventes en attente') },
    { id: 'abandoned', label: t('Ventes abandonnées') },
  ];
  return (
    <div role="tablist" aria-label={t('Filtrer le journal')} style={{ display: 'flex', gap: 2, marginBottom: 16, borderBottom: '1px solid var(--ink-200)',
      // ⚠️ `overflowY` EXPLICITE : `overflow-x: auto` SEUL fait passer l'autre axe de `visible` à
      //    `auto` (la spec l'impose). Les onglets font 40 px plus une bordure de 2 px tirée d'un
      //    pixel par `marginBottom: -1` — ça déborde, et un ascenseur VERTICAL apparaissait sur une
      //    rangée qui n'a qu'une ligne.
      overflowX: 'auto', overflowY: 'hidden' }}>
      {items.map((it, i) => {
        const actif = it.id === current;
        return (
          <button
            key={it.id}
            role="tab"
            id={`bo-journal-onglet-${it.id}`}
            aria-selected={actif}
            aria-controls="bo-journal-panneau"
            // ⚠️ TABULATION GLISSANTE + flèches : `role="tab"` sans elles annonce un motif d'onglets
            // qui n'existe pas — la tabulation traverse alors les trois boutons un par un, et les
            // flèches, que le lecteur d'écran a annoncées, ne font rien.
            tabIndex={actif ? 0 : -1}
            onKeyDown={(e) => {
              const pas = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
              if (!pas) return;
              e.preventDefault();
              const suivant = items[(i + pas + items.length) % items.length]!;
              onChange(suivant.id);
              // ⚠️ UN IDENTIFIANT DE DOM, JAMAIS UN TEXTE : traduit, il ne désignerait plus aucun
              //    élément et le déplacement au clavier cesserait de fonctionner, sans un mot.
              document.getElementById(`bo-journal-onglet-${suivant.id}`)?.focus();
            }}
            onClick={() => onChange(it.id)}
            style={{
              display: 'flex', alignItems: 'center', gap: 7, height: 40, padding: '0 15px',
              border: 'none', background: 'transparent', cursor: 'pointer',
              fontSize: '13.5px', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0,
              color: actif ? 'var(--teal-800)' : 'var(--ink-500)',
              borderBottom: `2px solid ${actif ? 'var(--teal-500)' : 'transparent'}`,
              marginBottom: -1,
            }}
          >
            {it.label}
            {/* ⚠️ UN COMPTEUR SUR LES QUATRE, comme la maquette — y compris à zéro. « Aucune vente
                annulée » est une information, et c'est même la meilleure nouvelle de l'écran ; ne
                l'afficher qu'au-delà de zéro obligerait à ouvrir l'onglet pour l'apprendre.
                ⚠️ `null` = pas encore mesuré : la pastille reste alors VIDE plutôt que d'annoncer 0. */}
            <Compteur valeur={comptes[it.id]} actif={actif} alerte={it.id !== 'all' && (comptes[it.id] ?? 0) > 0} />
          </button>
        );
      })}
    </div>
  );
}

/**
 * LE CHAMP DE RECHERCHE de la barre de filtres — loupe à l'intérieur, comme la maquette.
 *
 * ⚠️ IL PREND TOUTE LA PLACE RESTANTE (`flex: 1`, plancher 210 px) : c'est le contrôle qu'on utilise
 * en premier, et le seul dont la valeur est libre. Les autres se lisent d'un coup d'œil ; celui-ci se
 * relit pendant qu'on tape.
 *
 * ⚠️ EN PILULE — ÉCART ASSUMÉ AVEC LA MAQUETTE (arbitrage utilisateur 2026-08-20), qui le dessine en
 * `r-sm` comme ses listes. La forme distingue le champ LIBRE des contrôles à valeurs fermées qui
 * l'entourent, au lieu de les ranger tous sous la même silhouette.
 *
 * ⚠️ La loupe est `pointer-events: none` : posée sur le champ, elle en avalerait le clic — on viserait
 * le contrôle sans le focaliser.
 */
function Recherche({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div style={{ position: 'relative', flex: 1, minWidth: 210 }}>
      <span aria-hidden style={{
        position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)',
        color: 'var(--ink-400)', display: 'flex', pointerEvents: 'none',
      }}>
        <Icon name="search" size={16} stroke={2} />
      </span>
      <input
        className="field-input"
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={t('N° de pièce, client, vendeur, montant')}
        aria-label={t('Rechercher une pièce')}
        style={{ width: '100%', height: 40, padding: '0 14px 0 38px', borderRadius: 'var(--r-pill)', fontSize: '13.5px' }}
      />
    </div>
  );
}

/**
 * LE COMPTEUR D'UN ONGLET — la pastille de la maquette (h19, min-w19, pilule, 11 px, graisse 800).
 *
 * ⚠️ `null` NE S'AFFICHE PAS « 0 ». Tant que la période n'a pas répondu, on ne sait pas combien il y
 * en a — annoncer zéro serait affirmer une absence qu'on n'a pas mesurée (§23.0). La pastille reste
 * alors vide, et sa place est tenue pour que les onglets ne sautent pas à l'arrivée des chiffres.
 */
function Compteur({ valeur, actif, alerte }: { valeur: number | null; actif: boolean; alerte: boolean }) {
  const fond = alerte ? 'var(--amber-bg)' : actif ? 'var(--teal-100)' : 'var(--ink-100)';
  const encre = alerte ? 'var(--amber-fg)' : actif ? 'var(--teal-800)' : 'var(--ink-500)';
  return (
    <span style={{
      height: 19, minWidth: 19, padding: '0 6px', borderRadius: 'var(--r-pill)',
      background: fond, color: encre, fontSize: '11px', fontWeight: 800,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      fontVariantNumeric: 'tabular-nums',
    }}>
      {valeur === null ? '' : valeur.toLocaleString('fr-FR')}
    </span>
  );
}


/**
 * Les bornes AAAAMMJJ de la période, dans le fuseau du NAVIGATEUR.
 * ⚠️ Aucune conversion UTC : `toISOString()` déplacerait la journée d'un cran une partie de l'année,
 * et le journal de 23 h afficherait celui du lendemain.
 * ⚠️ L'INSTANT EST REÇU, jamais lu ici : c'est ce qui permet à l'écran de rebasculer à minuit au lieu
 * de figer la journée du premier rendu.
 */




/** L'arithmétique du pied : ce que le total RETIENT, et ce qu'il exclut. */
function Arithmetique({ s, period }: { s: { pieces: number; cancelledCount: number; creditCount: number; creditCents: number; unrecordedTaxCount?: number }; period: string }) {
  // ⚠️ DES CLÉS COMPTÉES, PAS DES TERNAIRES : le nombre part BRUT (le moteur de pluriel choisit sur
  //    un NOMBRE, pas sur « 1 234 »), et le nom accordé reste DANS la phrase — un traducteur qui
  //    reçoit « pièces » tout seul ne sait ni de quoi il s'agit, ni à quoi ça s'accorde.
  const bouts = [t('{n} pièce sur la période ({v})', { n: s.pieces, v: period.toLowerCase() })];
  if (s.creditCount > 0) {
    bouts.push(t('{n} avoir/retour compté pour {v}', { n: s.creditCount, v: formatMoney(s.creditCents, { thousandsSep: ' ' }) }));
  }
  if (s.cancelledCount > 0) {
    bouts.push(t('{n} vente annulée, exclue', { n: s.cancelledCount }));
  }
  // ⚠️ LE « — » DU PIED SE JUSTIFIE : sans cette phrase, un total HT ou TVA absent se lirait comme une panne.
  if ((s.unrecordedTaxCount ?? 0) > 0) {
    bouts.push(t('{n} pièce sans TVA renseignée par son logiciel d’origine', { n: s.unrecordedTaxCount ?? 0 }));
  }
  return <>{bouts.join(' · ')}</>;
}

/** Les colonnes de la liste — la maquette, moins ce qui n'existe pas en base. */
function buildColumns(): ColumnDef<SalesRow>[] {
  return [
    {
      key: 'heure', label: t('Date & heure'), width: 148, sortable: true, defaultSortDir: 'desc', mono: true,
      render: (r) => <Horodatage valeur={r.datetimeFiscal} />,
    },
    {
      key: 'piece', label: t('N° de pièce'), width: 132, sortable: true, mono: true, locked: true,
      render: (r) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {r.pieceNumber || <Absent />}
          <TypePiece row={r} />
        </span>
      ),
    },
    // ⚠️ « CAISSE » ET NON « Cse » — écart ASSUMÉ avec la maquette (arbitrage utilisateur 2026-08-20) :
    //    une abréviation de trois lettres économise 18 px et coûte une hésitation à chaque nouveau
    //    lecteur. La largeur suit le mot : 46 px ne logeaient pas l'en-tête, qui l'aurait élargie de
    //    lui-même — autant l'écrire.
    { key: 'caisse', label: t('Caisse'), width: 72, sortable: true, render: (r) => <Caisse id={r.registerId} /> },
    { key: 'operateur', label: t('Opérateur'), width: 140, sortable: true, render: (r) => <Vendeur nom={r.operatorName} /> },
    { key: 'client', label: t('Client'), width: 160, sortable: true, render: (r) => <ClientCellule nom={r.clientName} /> },
    {
      // ⚠️ LA COLONNE « Art. » DE LA MAQUETTE — la quantité vendue, pas le nombre de lignes : deux
      //    exemplaires du même article font 2, et c'est ce qu'un exploitant compte.
      key: 'articles', label: t('Art.'), width: 60, sortable: true, defaultSortDir: 'desc',
      numeric: true, align: 'end',
      // ⚠️ Les millièmes ne s'affichent QUE s'ils portent une information : « 3,000 » pour trois
      //    pièces se lit comme une précision qui n'existe pas.
      render: (r) => <Articles qtyMilli={r.qtyMilli} />,
    },
    {
      key: 'reglement', label: t('Règlement'), width: 150,
      // ⚠️ UNE VENTE ANNULÉE N'A PAS DE RÈGLEMENT — la maquette y affichait « Carte bancaire », ce qui
      // était faux. La colonne porte alors qui a annulé : c'est ce que l'exploitant cherche.
      render: (r) => (r.cancelledBy
        ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, color: 'var(--amber-fg)', fontSize: '12.5px', minWidth: 0 }}>
            <span style={{ display: 'flex', flexShrink: 0 }}><Icon name="alert" size={15} stroke={1.9} /></span>
            <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t('Annulée par {cancelledBy}', { cancelledBy: r.cancelledBy })}</span>
          </span>
        )
        : (r.methods.length ? <Reglements codes={r.methods} /> : <Absent />)),
    },
    {
      // ⚠️ AVANT LE TTC, comme sur un ticket : HT, TVA, puis ce qui a été payé.
      key: 'ht', label: t('Total HT'), width: 110, sortable: true, numeric: true, align: 'end',
      render: (r) => <Montant cents={r.totalExclTaxCents} barre={!!r.cancelledBy} />,
    },
    {
      key: 'tva', label: t('TVA'), width: 96, sortable: true, numeric: true, align: 'end',
      headerTitle: t('La TVA que la pièce porte. « — » : le logiciel d’origine d’une pièce reprise ne l’a pas renseignée.'),
      render: (r) => <Montant cents={r.totalTaxCents} barre={!!r.cancelledBy} />,
    },
    {
      key: 'total', label: t('Total TTC'), width: 120, sortable: true, numeric: true, align: 'end',
      render: (r) => (r.cancelledBy
        ? <span style={{ textDecoration: 'line-through', color: 'var(--ink-400)' }}>{formatMoney(r.totalInclTaxCents, { thousandsSep: ' ' })}</span>
        : <strong style={{ fontWeight: 600 }}>{formatMoney(r.totalInclTaxCents, { thousandsSep: ' ' })}</strong>),
    },
  ];
}

function Reglements({ codes }: { codes: string[] }) {
  // ⚠️ UNE SEULE ICÔNE, CELLE DU PREMIER MOYEN : une pièce réglée en trois fois alignerait trois
  //    dessins et la colonne cesserait de se lire d'un coup d'œil. Les libellés, eux, sont tous là.
  const premier = codes[0] ? REGLEMENTS[codes[0]] : undefined;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
      <span style={{ display: 'flex', flexShrink: 0, color: premier?.tone ?? 'var(--ink-400)' }}>
        <Icon name={premier?.icon ?? 'wallet'} size={15} stroke={1.9} />
      </span>
      <span style={{ fontSize: '12.5px', color: 'var(--ink-600)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {codes.map(libelleReglement).join(' + ')}
      </span>
    </span>
  );
}

/**
 * CE QUI N'EST PAS UN TICKET DE VENTE ORDINAIRE — nature de la pièce, annulation, duplicata tirés.
 *
 * ⚠️ TOUT EST À CÔTÉ DU NUMÉRO, comme la maquette : ce sont des propriétés de LA PIÈCE, et les
 * disperser dans trois colonnes obligerait à balayer la ligne pour savoir ce qu'on regarde.
 * ⚠️ PAS `form="code"` : la forme mono/capitales est réservée aux identifiants techniques. « AVOIR »
 * et « FACTURE » sont des mots du métier — en mono, ils se lisent comme un code de dictionnaire.
 * ⚠️ Divergence de teinte assumée : la maquette peint l'avoir en ROSE, ton absent de la palette de
 * pastilles (elle porte des RÔLES, pas des couleurs). `violet` est le plus proche, et l'ajout d'un
 * ton pour une seule pastille coûterait plus qu'il ne rapporte.
 */
function TypePiece({ row }: { row: SalesRow }) {
  // ⚠️ LES DEUX RÈGLES VIENNENT DU CŒUR : elles étaient écrites ici en TypeScript et une seconde fois
  //    en SQL au serveur. Sur la base réelle `AVO|RET` et `AVO|ANN` coexistent — deux copies auraient
  //    divergé sur exactement ce cas, chacune restant cohérente avec elle-même.
  const avoir = isRefundPiece(row.pieceType, row.operationType);
  // ⚠️ LE TYPE SEUL NE SUFFIT PAS : une facture d'avoir porte le type des avoirs et un numéro de la série des factures —
  //    lue au seul type, elle passait pour l'avoir d'un ticket.
  const facture = isInvoiceDocument(row.pieceType, row.pieceNumber);
  const dup = row.reprintCount > 0;
  if (!avoir && !facture && !row.cancelledBy && !dup) return null;
  return (
    <>
      {facture && <Badge tone="info" size="xs" style={{ letterSpacing: '.02em' }}>{t('FACTURE')}</Badge>}
      {avoir && <Badge tone="violet" size="xs" style={{ letterSpacing: '.02em' }}>{t('AVOIR')}</Badge>}
      {row.cancelledBy && <Badge tone="neutral" size="xs" style={{ letterSpacing: '.02em' }}>{t('ANNULÉ')}</Badge>}
      {/* ⚠️ LE COMPTE DES DUPLICATA SE VOIT SUR LA LIGNE, pas seulement en ouvrant la pièce : un
          ticket rejoué trois fois est un signal d'exploitation, et on ne le cherche pas, on le croise. */}
      {/* ⚠️ LE TROU QUI PORTE LE COMPTE S'APPELLE `n` : c'est lui que le moteur de pluriel lit.
          Nommé autrement, la phrase se fige au pluriel et un seul duplicata s'annonce « 1 duplicata
          déjà tirés » — à la synthèse vocale, la seule qui lise ce libellé. */}
      {dup && (
        <Badge tone="neutral" size="xs" style={{ letterSpacing: '.02em' }} srLabel={t('{n} duplicata déjà tiré', { n: row.reprintCount })}>
          {t('DUP ×{reprintCount}', { reprintCount: row.reprintCount })}
        </Badge>
      )}
    </>
  );
}

/**
 * Un montant HT ou TVA de la pièce, barré quand une annulation l'a contre-passée.
 * ⚠️ `null` = le logiciel d'origine d'une pièce reprise ne l'a pas renseigné : « — », jamais un zéro qui contredirait le
 *    TTC de la même ligne.
 */
function Montant({ cents, barre }: { cents: number | null; barre: boolean }) {
  if (cents === null) return <span style={{ color: 'var(--ink-300)' }} title={t('Non renseigné par le logiciel d’origine de la pièce')}>—</span>;
  const v = formatMoney(cents, { thousandsSep: ' ' });
  return barre ? <span style={{ textDecoration: 'line-through', color: 'var(--ink-400)' }}>{v}</span> : <>{v}</>;
}

/** Une donnée ABSENTE de la base — distincte d'une valeur vide, et jamais rendue par un zéro. */
function Absent() {
  return <span style={{ color: 'var(--ink-300)' }} title={t('Absent de la pièce scellée')}>—</span>;
}

/**
 * LE DÉTAIL D'UNE PIÈCE — lignes, règlements, ventilation TVA, empreinte fiscale, duplicata tirés.
 * ⚠️ Lu à l'ouverture, jamais préchargé pour toute la page : 14 détails de pièce à chaque page, c'est
 * 70 requêtes pour une ligne qu'on ouvrira peut-être.
 */
function Piece({ id }: { id: string }) {
  const [detail, setDetail] = useState<SalesDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let vivant = true;
    api.salesPiece(id)
      .then((d) => { if (vivant) { setDetail(d); setError(null); } })
      .catch((e: unknown) => { if (vivant) setError(e instanceof Error ? e.message : String(e)); });
    return () => { vivant = false; };
  }, [id]);

  if (error) {
    return (
      <div role="alert" style={{ padding: '10px 4px', color: 'var(--danger-ink)', fontSize: 'var(--t-note)' }}>
        {t('Le détail de cette pièce n’a pas pu être lu : {error}', { error })}
      </div>
    );
  }
  if (!detail) return <div style={{ padding: '10px 4px', color: 'var(--ink-500)', fontSize: 'var(--t-note)' }}>{t('Lecture de la pièce…')}</div>;
  const { piece, lines, payments, taxes } = detail;
  const codes = payments.map((p) => p.methodCode);
  const premier = codes[0] ? REGLEMENTS[codes[0]] : undefined;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* ⚠️ LE VERROU SE DIT EN PREMIER : c'est la contrainte qui explique l'absence de tout bouton
             d'édition plus bas. Sans elle, l'écran se lit comme un formulaire qu'on n'a pas fini. */}
      <Encart tone="neutre" icone="lock">
        
        {t('Pièce signée par la caisse')} <strong style={{ color: 'var(--ink-900)' }}>{piece.registerId ? codeCaisse(piece.registerId) : '—'}</strong>.
        {' '}{t('Cette vente ne peut être ni modifiée, ni supprimée.')}
      </Encart>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 11 }}>
        <Fiche heading={t('Opérateur')} valeur={piece.operatorName || '—'} />
        <Fiche
          heading={t('Client')}
          valeur={piece.clientName || t('Non identifié')}
          pale={!piece.clientName}
        />
      </div>

      {piece.cancelledBy && (
        <Encart tone="alerte" icone="alert">
          
          {/* ⚠️ DEUX PHRASES ENTIÈRES, la mise en forme portée par les marqueurs : la phrase était coupée en quatre
              morceaux (« Vente annulée par », le nom, « le {v} », « et ne compte pas… ») que le traducteur recevait
              séparément, et dont l'ordre restait celui du français dans toutes les langues. */}
          {piece.cancelledAt
            ? <Trans k='Vente annulée par <1>{qui}</1> le {date}.' vars={{ qui: piece.cancelledBy, date: horodatageFiscal(piece.cancelledAt) }} parts={[(x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>]} />
            : <Trans k='Vente annulée par <1>{qui}</1>.' vars={{ qui: piece.cancelledBy }} parts={[(x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>]} />}
          {' '}
          <Trans k='<1>Le document reste au journal</1> et ne compte pas dans le chiffre d’affaires : rien ne disparaît d’une chaîne fiscale.' parts={[(x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>]} />
        </Encart>
      )}

      <Carte heading={t('Lignes')}>
        {lines.length === 0
          ? <div style={{ padding: '14px', fontSize: '12.5px', color: 'var(--ink-500)' }}>{t('Cette pièce ne porte aucune ligne en base.')}</div>
          : lines.map((l) => (
            <div key={l.lineNo} style={{
              display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 34px 72px 80px', gap: 8,
              padding: '9px 14px', alignItems: 'center', borderBottom: '1px solid var(--ink-100)', fontSize: '13px',
            }}>
              <span style={{ minWidth: 0 }}>
                <span style={{ display: 'block', fontWeight: 600, color: 'var(--ink-900)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {l.label}
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontFamily: 'var(--font-mono)', fontSize: '10.5px', color: 'var(--ink-400)', marginTop: 1 }}>
                  {/* ⚠️ LE GENCODE SE COPIE EN CLIQUANT LA VALEUR (charte §5) : c'est une identité
                      technique, et on la recopie à la main sinon. */}
                  {l.gencode ? <Copyable value={l.gencode} label={t('Copier le gencode')}>{l.gencode}</Copyable> : <span>{t('sans code-barres')}</span>}
                  <span>{t('· TVA {v} %', { v: formatMinor(l.vatRateBp, { scale: 2, displayScale: 1 }) })}</span>
                  {/* ⚠️ La remise de ligne est DITE ici, et pas seulement soustraite : un prix barré
                      sans son motif se lit comme une erreur de saisie. */}
                  {l.lineDiscountCents > 0 && <span>{t('· remise {v}', { v: formatMoney(l.lineDiscountCents, { thousandsSep: ' ' }) })}</span>}
                </span>
              </span>
              <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--ink-600)' }}>
                {formatMinor(l.qtyMilli, { scale: 3, displayScale: l.qtyMilli % 1000 === 0 ? 0 : 3 })}
              </span>
              <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--ink-600)', fontSize: '12.5px' }}>
                {formatMoney(l.unitPriceInclTaxCents, { thousandsSep: ' ' })}
              </span>
              <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: 'var(--ink-900)' }}>
                {formatMoney(l.totalInclTaxCents, { thousandsSep: ' ' })}
              </span>
            </div>
          ))}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px', background: 'var(--ink-50)' }}>
          <span style={{ flex: 1, fontSize: '13px', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Total TTC')}</span>
          <span style={{ fontSize: '16px', fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: 'var(--ink-900)' }}>
            {formatMoney(piece.totalInclTaxCents, { thousandsSep: ' ' })}
          </span>
        </div>
      </Carte>

      <Carte>
        <div style={{
          display: 'grid', gridTemplateColumns: '62px minmax(0,1fr) 78px 78px', gap: 8,
          padding: '10px 14px', background: 'var(--ink-50)', borderBottom: '1px solid var(--ink-200)',
          fontSize: '10.5px', fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--ink-500)',
        }}>
          <span>{t('Taux')}</span><span>{t('Base HT')}</span>
          <span style={{ textAlign: 'right' }}>TVA</span><span style={{ textAlign: 'right' }}>TTC</span>
        </div>
        {taxes.length === 0
          ? <div style={{ padding: 14, fontSize: '12.5px', color: 'var(--ink-500)' }}>{t('Aucune ventilation de TVA sur cette pièce.')}</div>
          : taxes.map((taxe, i) => (
            <div key={i} style={{
              display: 'grid', gridTemplateColumns: '62px minmax(0,1fr) 78px 78px', gap: 8,
              padding: '9px 14px', alignItems: 'center', borderBottom: '1px solid var(--ink-100)',
              fontSize: '12.5px', fontVariantNumeric: 'tabular-nums',
            }}>
              <span title={taxe.vatCode ? t('Code TVA {v}', { v: taxe.vatCode }) : t('Code TVA absent')} style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--ink-900)' }}>
                {formatMinor(taxe.rateBp, { scale: 2, displayScale: 1 })} %
              </span>
              <span style={{ color: 'var(--ink-600)' }}>{formatMoney(taxe.baseExclTaxCents, { thousandsSep: ' ' })}</span>
              <span style={{ textAlign: 'right', color: 'var(--ink-600)' }}>{formatMoney(taxe.taxCents, { thousandsSep: ' ' })}</span>
              <span style={{ textAlign: 'right', fontWeight: 600, color: 'var(--ink-900)' }}>
                {formatMoney(taxe.baseExclTaxCents + taxe.taxCents, { thousandsSep: ' ' })}
              </span>
            </div>
          ))}
        <div style={{ padding: '10px 14px', background: 'var(--ink-50)', fontSize: '12.5px', color: 'var(--ink-600)', lineHeight: 1.55 }}>
          <Trans k='Les taux sont ceux <1>figés à la vente</1>. Changer un taux dans les réglages ne réécrit pas cette ventilation.' parts={[(x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>]} /></div>
      </Carte>

      {/* ⚠️ LE RÈGLEMENT EST UN ENCART, PAS UNE LIGNE DE TABLEAU : c'est la réponse à « comment cette
             vente a-t-elle été payée », et elle se lit d'un coup d'œil. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '12px 14px', borderRadius: 'var(--r-sm)', border: '1px solid var(--ink-200)' }}>
        <span aria-hidden style={{
          width: 34, height: 34, borderRadius: 'var(--r-ctrl)', background: 'var(--ink-50)',
          border: '1px solid var(--ink-200)', color: premier?.tone ?? 'var(--ink-400)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Icon name={premier?.icon ?? 'wallet'} size={17} stroke={1.9} />
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: '10.5px', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ink-500)' }}>
            
            {t('Règlement')}
          </span>
          <span style={{ display: 'block', fontSize: '13.5px', fontWeight: 600, color: 'var(--ink-900)', marginTop: 3 }}>
            {payments.length === 0 ? t('Aucun règlement sur cette pièce') : payments.map((p) => libelleReglement(p.methodCode)).join(' + ')}
          </span>
          {/* ⚠️ LE DÉTAIL DES MONTANTS DÈS QU'IL Y EN A PLUSIEURS : « Carte + Espèces » sans ses deux
              montants ne permet pas de rapprocher un fonds de caisse. */}
          {payments.length > 1 && (
            <span style={{ display: 'block', fontSize: '11.5px', color: 'var(--ink-500)', marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>
              {payments.map((p) => `${libelleReglement(p.methodCode)} ${formatMoney(p.amountCents, { thousandsSep: ' ' })}`).join(' · ')}
            </span>
          )}
        </span>
        <span style={{ fontSize: '14px', fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: 'var(--ink-900)', flexShrink: 0 }}>
          {formatMoney(piece.totalInclTaxCents, { thousandsSep: ' ' })}
        </span>
      </div>

      <div style={{ padding: '12px 14px', borderRadius: 'var(--r-sm)', background: 'var(--ink-50)', border: '1px solid var(--ink-200)', fontSize: '12.5px', color: 'var(--ink-600)', lineHeight: 1.6 }}>
        <Trans k='<1>Pas d’avoir depuis le back-office.</1> Un avoir est une pièce fiscale : <2>seule une caisse peut la signer</2>. Il se fait au comptoir, en rappelant ce numéro de pièce — le journal l’affichera alors comme un document lié, jamais comme une correction de celui-ci.' parts={[(x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>, (x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>]} /></div>
    </div>
  );
}


/**
 * LES VENTES EN ATTENTE — les paniers suspendus sur les caisses, en lecture stricte.
 *
 * ⚠️ CE N'EST PAS UNE LISTE DE PIÈCES, et l'écran le dit AVANT de montrer quoi que ce soit : un panier
 * suspendu n'a ni numéro ni signature, il n'entre dans aucun total fiscal, et il vit sur SA caisse. Le
 * back-office ne peut ni le reprendre ni l'abandonner — cela se fait au comptoir.
 *
 * ⚠️ MÊME `DataTable` QUE LE JOURNAL, et ce n'est pas cosmétique : tri, densité, colonnes, états vides
 * et messages d'échec viennent du socle partagé. Une seconde recette de tableau ici aurait divergé dès
 * la première amélioration apportée à l'autre.
 *
 * ⚠️ LE PLUS ANCIEN EN TÊTE, à l'inverse du journal : ici l'urgence est l'âge, pas la fraîcheur.
 */
function Attente({ vue, erreur, onRetry }: {
  vue: PendingSalesView | null; erreur: string | null; onRetry: () => void;
}) {
  // L'âge se recalcule chaque minute, sans quoi « il y a 3 min » resterait vrai une heure durant.
  const now = useHorlogeMinute();
  const colonnes = useMemo(() => colonnesAttente(now), [now]);

  // ⚠️ « CE MAGASIN NE REMONTE PAS ENCORE » N'EST PAS « AUCUN PANIER » : la seconde phrase serait une
  //    garantie qu'on ne peut pas donner tant que les caisses n'ont pas poussé ce canal. Rendu hors
  //    table, parce que ce n'est pas un état du tableau mais du magasin.
  if (vue && !vue.supported) {
    return (
      <EmptyState
        icon="clock"
        title={t('Les paniers suspendus ne remontent pas encore de ce magasin')}
        note={t('Ses caisses n’ont pas encore poussé ce canal — ce n’est pas la même chose qu’aucun panier en attente. Rien ne peut être affirmé ici tant qu’elles ne l’ont pas fait.')}
      />
    );
  }

  const rows = vue?.rows ?? [];
  const total = rows.reduce((n, r) => n + r.totalInclTaxCents, 0);
  const status: TableStatus = erreur
    ? {
      phase: 'error',
      message: erreur,
      detail: t('Rien n’est affiché tant que la lecture n’a pas abouti : un panier suspendu invisible est de l’argent qu’on croit encaissé.'),
      onRetry,
    }
    : vue === null ? { phase: 'loading' } : { phase: 'ready' };

  return (
    <>
      <div style={{
        display: 'flex', gap: 10, padding: '12px 16px', alignItems: 'flex-start', marginBottom: 12,
        background: 'var(--amber-bg)', color: 'var(--amber-fg)',
        border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)',
      }}>
        <Icon name="clock" size={17} />
        <div style={{ fontSize: '12.5px', lineHeight: 1.55 }}>
          <strong>
            {rows.length === 0
              ? t('Aucune vente en attente sur les caisses')
              // ⚠️ UNE CLÉ COMPTÉE, PAS UN TERNAIRE : le pluriel était choisi à la main entre deux
              //    `t()`, ce qui livre au traducteur deux moitiés de nom sans leur phrase et ne
              //    marche que pour les langues à DEUX formes. C'est le catalogue qui porte l'accord.
              : t('{n} vente en attente sur les caisses — {v} de paniers suspendus', { n: rows.length, v: formatMoney(total, { thousandsSep: ' ' }) })}
          </strong>
          <div style={{ marginTop: 2 }}>
            <Trans k='Une vente en attente <1>n’est pas encore une pièce fiscale</1> : elle vit sur sa caisse, sans numéro ni signature. Elle se reprend ou s’abandonne <2>au comptoir</2> — le back-office la montre, il n’y touche pas.' parts={[(x) => <strong>{x}</strong>, (x) => <strong>{x}</strong>]} /></div>
        </div>
      </div>

      <DataTable<PendingSale>
        tableId="bo-journal-attente"
        unit={{ one: t('panier'), gender: 'm' }}
        columns={colonnes}
        rows={rows}
        getRowId={(r) => r.id}
        status={status}
        rowTone={(r) => (now.getTime() - r.heldAt >= 3_600_000 ? 'pending' : undefined)}
        totalRow={{
          cells: { montant: <strong>{formatMoney(total, { thousandsSep: ' ' })}</strong> },
          note: <>{t('Une attente abandonnée en fin de journée est signalée sur le Z de sa caisse — aucune ne peut traverser la clôture sans décision.')}</>,
        }}
        emptyStates={{
          empty: {
            icon: 'clock',
            title: t('Aucun panier n’attend sur les caisses'),
            note: t('Les ventes mises de côté au comptoir apparaissent ici jusqu’à ce qu’elles soient reprises ou abandonnées. Aucune n’est en cours pour l’instant.'),
          },
          noMatch: {
            title: t('Aucun panier ne correspond'),
            note: t('Aucun filtre n’est posé sur cette liste — si ce message s’affiche, c’est qu’il y a un défaut à signaler.'),
          },
        }}
      />
    </>
  );
}

/**
 * Les colonnes des paniers suspendus — celles de la maquette, dans son ordre.
 * ⚠️ L'INSTANT EST PASSÉ EN PARAMÈTRE plutôt que lu dans chaque cellule : une horloge par ligne ferait
 * battre quatorze minuteries au lieu d'une, et elles ne changeraient pas de minute ensemble.
 */
function colonnesAttente(now: Date): ColumnDef<PendingSale>[] {
  return [
    {
      key: 'age', label: t('Depuis'), width: 92, mono: true,
      render: (r) => <Age depuis={r.heldAt} now={now} />,
    },
    // ⚠️ MÊME MOT QUE LE JOURNAL : la même colonne nommée de deux façons sur un même écran ferait
    //    croire à deux données distinctes.
    { key: 'caisse', label: t('Caisse'), width: 72, mono: true, render: (r) => (r.registerId ? codeCaisse(r.registerId) : <Absent />) },
    { key: 'operateur', label: t('Opérateur'), width: 130, render: (r) => r.operatorName || <Absent /> },
    {
      key: 'client', label: t('Client'), width: 150,
      render: (r) => (r.clientName || <span style={{ color: 'var(--ink-400)' }}>{t('Comptoir')}</span>),
    },
    // ⚠️ LA COLONNE SANS SOCLE : c'est elle qui absorbe le reliquat de largeur du tableau.
    { key: 'contenu', label: t('Contenu'), maxWidth: 380, render: (r) => (r.contentSummary || <Absent />) },
    {
      key: 'articles', label: t('Art.'), width: 56, numeric: true, align: 'end', mono: true,
      render: (r) => formatMinor(r.qtyMilli, { scale: 3, displayScale: r.qtyMilli % 1000 === 0 ? 0 : 3 }),
    },
    {
      key: 'montant', label: t('Montant'), width: 110, numeric: true, align: 'end', mono: true,
      render: (r) => formatMoney(r.totalInclTaxCents, { thousandsSep: ' ' }),
    },
  ];
}

/** Les motifs qu'une caisse enregistre, par leur CLÉ — une donnée persistée, jamais traduite ; seul le libellé l'est. */
const MOTIFS_ABANDON: Record<string, Translatable> = {
  'ticket_vidé': key('Ticket vidé'),
  'ticket_vidé_paiement_engagé': key('Ticket vidé, paiement engagé'),
  'vente_en_attente_supprimée': key('Vente en attente supprimée'),
};

/**
 * Le motif d'un abandon, dans la langue du compte.
 * ⚠️ UNE CLÉ INCONNUE S'AFFICHE TELLE QUELLE : une caisse plus récente peut en écrire une nouvelle, et la taire ferait
 *    croire à un abandon sans motif.
 */
function motifAbandon(r: AbandonedRow): string {
  if (!r.reason) return r.fromLegacy ? t('Reprise de l’ancien logiciel') : '—';
  const m = MOTIFS_ABANDON[r.reason];
  return m ? t(m) : r.reason;
}

/**
 * LES VENTES ABANDONNÉES — les paniers vidés avant d'être encaissés, sur la période du journal.
 *
 * ⚠️ CE NE SONT PAS DES VENTES, et l'écran le dit avant de montrer quoi que ce soit : rien n'a été scellé, aucun
 *    montant n'entre dans le chiffre. Le montant est celui que la caisse ESTIMAIT au moment de l'abandon.
 * ⚠️ LE RÉSUMÉ PAR VENDEUR vient du serveur, sur exactement les lignes filtrées : c'est la répétition chez un même
 *    vendeur qui signale la fraude, et l'écran ne recompte rien.
 * ⚠️ UNE LIGNE AVEC UN PAIEMENT ENGAGÉ est teintée : c'est le signal fort, celui qu'on ne doit pas avoir à chercher.
 */
function Abandons({ vue, status, pageIndex, onPageChange, onOpen, toolbar }: {
  vue: AbandonedPageView | null; status: TableStatus; pageIndex: number; onPageChange: (i: number) => void;
  onOpen: (r: AbandonedRow) => void; toolbar: ReactNode;
}) {
  const colonnes = useMemo(() => colonnesAbandons(), []);
  // ⚠️ `?.` À CHAQUE NIVEAU, comme la page du journal : une réponse incomplète donne un tableau vide, jamais un écran blanc.
  const s = vue?.summary ?? null;
  return (
    <>
      <div style={{
        display: 'flex', gap: 10, padding: '12px 16px', alignItems: 'flex-start', marginBottom: 12,
        background: 'var(--amber-bg)', color: 'var(--amber-fg)',
        border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)',
      }}>
        <Icon name="alert" size={17} />
        <div style={{ fontSize: '12.5px', lineHeight: 1.55 }}>
          {s && (
            <strong>
              {s.count === 0
                ? t('Aucune vente abandonnée sur cette période')
                : t('{n} vente abandonnée sur la période, pour {v} estimés', { n: s.count, v: formatMoney(s.totalCents, { thousandsSep: ' ' }) })}
              {s.paymentStartedCount > 0 && <>{' · '}{t('{n} avec un paiement engagé', { n: s.paymentStartedCount })}</>}
            </strong>
          )}
          <div style={{ marginTop: s ? 2 : 0 }}>
            <Trans k='Une vente abandonnée <1>n’est pas une vente</1> : un panier vidé, ou une vente en attente supprimée, avant d’être encaissé — rien n’a été scellé. La caisse en garde la trace ; c’est la <2>répétition chez un même vendeur</2>, surtout avec un paiement engagé, qui doit alerter.' parts={[(x) => <strong>{x}</strong>, (x) => <strong>{x}</strong>]} />
          </div>
        </div>
      </div>

      {vue?.byOperator?.length ? <ParVendeur lignes={vue.byOperator} /> : null}

      <DataTable<AbandonedRow>
        tableId="bo-journal-abandons"
        unit={{ one: t('vente abandonnée'), gender: 'f' }}
        columns={colonnes}
        rows={vue?.rows ?? []}
        getRowId={(r) => r.id}
        status={status}
        page={{ index: vue?.page?.index ?? pageIndex, size: vue?.page?.size ?? PAGE_SIZE, total: s?.count ?? 'unknown' }}
        onPageChange={onPageChange}
        rowTone={(r) => (r.paymentStarted ? 'pending' : undefined)}
        onRowActivate={onOpen}
        totalRow={s ? {
          cells: { montant: <strong>{formatMoney(s.totalCents, { thousandsSep: ' ' })}</strong> },
          note: <>{t('Montants estimés par la caisse au moment de l’abandon — rien n’a été encaissé. Une vente reprise de l’ancien logiciel porte la somme des règlements qu’elle avait engagés.')}</>,
        } : undefined}
        toolbar={toolbar}
        filtersActive
        emptyStates={{
          empty: {
            icon: 'receipt',
            title: t('Aucune vente abandonnée dans ce magasin'),
            note: t('Un panier vidé avant d’être encaissé apparaît ici, avec qui l’a vidé, quand, et si un paiement était engagé.'),
          },
          noMatch: {
            title: t('Aucune vente abandonnée sur cette période'),
            note: t('Élargissez la période ou effacez les filtres.'),
          },
        }}
      />
    </>
  );
}

/** Les colonnes des ventes abandonnées — les mots du journal pour ce qu'ils ont en commun. */
function colonnesAbandons(): ColumnDef<AbandonedRow>[] {
  return [
    { key: 'heure', label: t('Date & heure'), width: 148, mono: true, render: (r) => <Horodatage valeur={r.voidedAtLocal} fiscal={false} /> },
    { key: 'caisse', label: t('Caisse'), width: 72, render: (r) => <Caisse id={r.registerId} /> },
    { key: 'operateur', label: t('Opérateur'), width: 140, render: (r) => <Vendeur nom={r.operatorName} /> },
    { key: 'motif', label: t('Motif'), maxWidth: 260, render: (r) => motifAbandon(r) },
    {
      key: 'paiement', label: t('Paiement'), width: 150,
      render: (r) => (r.paymentStarted ? <Badge tone="warn">{t('Paiement engagé')}</Badge> : <Absent />),
    },
    { key: 'lignes', label: t('Lignes'), width: 64, numeric: true, align: 'end', mono: true, render: (r) => r.lineCount },
    {
      key: 'montant', label: t('Montant estimé'), width: 130, numeric: true, align: 'end', mono: true,
      render: (r) => formatMoney(r.estimatedTotalInclTaxCents, { thousandsSep: ' ' }),
    },
  ];
}

/** Le résumé par vendeur — le plus d'abandons en tête, tel que le serveur l'a compté sur les lignes filtrées. */
function ParVendeur({ lignes }: { lignes: AbandonedPageView['byOperator'] }) {
  const grille = 'minmax(140px,1.4fr) 90px 130px 150px';
  const tete: CSSProperties = { fontSize: '11px', fontWeight: 700, color: 'var(--ink-500)', textTransform: 'uppercase', letterSpacing: '.05em' };
  return (
    <div role="table" aria-label={t('Ventes abandonnées par vendeur')} className="dcard" style={{ padding: '10px 16px', marginBottom: 12 }}>
      <div role="row" style={{ display: 'grid', gridTemplateColumns: grille, gap: 12, padding: '4px 0 8px', borderBottom: '1px solid var(--ink-100)' }}>
        <span role="columnheader" style={tete}>{t('Vendeur')}</span>
        <span role="columnheader" style={{ ...tete, textAlign: 'right' }}>{t('Abandons')}</span>
        <span role="columnheader" style={{ ...tete, textAlign: 'right' }}>{t('Montant estimé')}</span>
        <span role="columnheader" style={{ ...tete, textAlign: 'right' }}>{t('Paiement engagé')}</span>
      </div>
      {lignes.map((l) => (
        <div key={l.operatorName} role="row" style={{ display: 'grid', gridTemplateColumns: grille, gap: 12, alignItems: 'center', padding: '7px 0', borderBottom: '1px solid var(--ink-100)', fontVariantNumeric: 'tabular-nums' }}>
          <span role="cell"><Vendeur nom={l.operatorName} /></span>
          <span role="cell" style={{ textAlign: 'right', fontWeight: 700 }}>{l.count}</span>
          <span role="cell" style={{ textAlign: 'right' }}>{formatMoney(l.totalCents, { thousandsSep: ' ' })}</span>
          <span role="cell" style={{ textAlign: 'right', fontWeight: l.paymentStartedCount > 0 ? 700 : 400, color: l.paymentStartedCount > 0 ? 'var(--amber-fg)' : 'var(--ink-500)' }}>
            {l.paymentStartedCount}
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * Le filtre « paiement engagé seulement » — un interrupteur à la hauteur des autres contrôles de la barre.
 * ⚠️ À L'ONGLET DES ABANDONS SEULEMENT : une pièce du journal a toujours été payée, le filtre n'y voudrait rien dire.
 */
function PaiementEngage({ actif, onChange }: { actif: boolean; onChange: (v: boolean) => void }) {
  return (
    <button type="button" aria-pressed={actif} onClick={() => onChange(!actif)} style={{
      display: 'inline-flex', alignItems: 'center', gap: 7, height: 40, padding: '0 14px', borderRadius: 'var(--r-pill)',
      border: `1px solid ${actif ? 'var(--amber)' : 'var(--ink-200)'}`, background: actif ? 'var(--amber-bg)' : 'var(--paper)',
      color: actif ? 'var(--amber-fg)' : 'var(--ink-700)', fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer',
    }}>
      <Icon name="alert" size={14} />
      {t('Paiement engagé seulement')}
    </button>
  );
}

/** La bande d'identité d'un panier abandonné : caisse, vendeur, motif, paiement engagé. */
function MetaAbandon({ row }: { row: AbandonedRow }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <Caisse id={row.registerId} />
      <Vendeur nom={row.operatorName} />
      <Badge tone="neutral">{motifAbandon(row)}</Badge>
      {row.paymentStarted && <Badge tone="warn">{t('Paiement engagé')}</Badge>}
    </span>
  );
}

/**
 * CE QU'IL Y AVAIT AU PANIER — les lignes telles que la caisse les a gardées.
 * ⚠️ AUCUN MONTANT N'EST CALCULÉ ICI : le prix unitaire et la quantité viennent de la trace, le total est celui que la
 *    caisse estimait. Un total de ligne recalculé à l'écran serait une seconde définition de ce que valait le panier.
 */
function PanierAbandonne({ row }: { row: AbandonedRow }) {
  const [detail, setDetail] = useState<AbandonedDetailView | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let vivant = true;
    setDetail(null); setError(null);
    api.salesAbandonedDetail(row.id)
      .then((d) => { if (vivant) setDetail(d); })
      .catch((e: unknown) => { if (vivant) setError(e instanceof Error ? e.message : String(e)); });
    return () => { vivant = false; };
  }, [row.id]);
  if (error) {
    return (
      <div role="alert" style={{ padding: 4, color: 'var(--danger-ink)', fontSize: 'var(--t-body)' }}>
        {t('Le panier n’a pas pu être lu : {error}.', { error })}
      </div>
    );
  }
  if (!detail) return <EmptyState icon="receipt" title={t('Lecture du panier…')} note={t('Les lignes sont lues dans la base du magasin.')} />;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div role="table" aria-label={t('Lignes du panier abandonné')} style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', overflow: 'hidden' }}>
        <div role="row" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 80px 120px', gap: 12, padding: '8px 12px', background: 'var(--ink-50)', fontSize: '11px', fontWeight: 700, color: 'var(--ink-500)', textTransform: 'uppercase', letterSpacing: '.05em' }}>
          <span role="columnheader">{t('Article')}</span>
          <span role="columnheader" style={{ textAlign: 'right' }}>{t('Qté')}</span>
          <span role="columnheader" style={{ textAlign: 'right' }}>{t('Prix unitaire')}</span>
        </div>
        {detail.lines.map((l) => (
          <div key={l.lineNo} role="row" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 80px 120px', gap: 12, padding: '8px 12px', borderTop: '1px solid var(--ink-100)', fontSize: '13px', fontVariantNumeric: 'tabular-nums' }}>
            <span role="cell" style={{ minWidth: 0 }}>
              <span style={{ display: 'block', color: 'var(--ink-900)', fontWeight: 600 }}>{l.label || <Absent />}</span>
              {l.gencode && <span style={{ display: 'block', fontFamily: 'var(--font-mono)', fontSize: '11px', color: 'var(--ink-500)' }}>{l.gencode}</span>}
            </span>
            <span role="cell" style={{ textAlign: 'right' }}>{formatMinor(l.qtyMilli, { scale: 3, displayScale: l.qtyMilli % 1000 === 0 ? 0 : 3 })}</span>
            <span role="cell" style={{ textAlign: 'right' }}>{formatMoney(l.unitPriceInclTaxCents, { thousandsSep: ' ' })}</span>
          </div>
        ))}
        {detail.lines.length === 0 && (
          <div role="row" style={{ padding: '10px 12px', fontSize: '12.5px', color: 'var(--ink-500)' }}>
            <span role="cell">{t('La caisse n’a gardé aucune ligne pour ce panier.')}</span>
          </div>
        )}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: '13px', color: 'var(--ink-600)' }}>
        <span>{row.fromLegacy ? t('Somme des règlements engagés (ancien logiciel)') : t('Total estimé par la caisse')}</span>
        <strong style={{ fontSize: '15px', color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums' }}>{formatMoney(row.estimatedTotalInclTaxCents, { thousandsSep: ' ' })}</strong>
      </div>
    </div>
  );
}

/**
 * DEPUIS COMBIEN DE TEMPS un panier attend.
 * ⚠️ CALCULÉ DANS LE FUSEAU DE QUI REGARDE, à partir d'un horodatage absolu : c'est la seule mesure
 * qui reste juste quand l'exploitant consulte depuis un autre pays que son magasin.
 */
function Age({ depuis, now }: { depuis: number; now: Date }) {
  const min = Math.max(0, Math.round((now.getTime() - depuis) / 60_000));
  const vieux = min >= 60;
  const texte = vieux ? `${Math.floor(min / 60)} h ${String(min % 60).padStart(2, '0')}` : `${min} min`;
  return (
    <span title={new Date(depuis).toLocaleString('fr-FR')} style={{ fontWeight: vieux ? 700 : 500, color: vieux ? 'var(--amber-fg)' : 'var(--ink-700)' }}>
      {texte}
    </span>
  );
}

// ══════════════════════════════════════════════════════════════════════════════════════════════
// LES CELLULES DU JOURNAL — portage PIXEL PERFECT de la maquette (§25, markup 11125-11158).
//
// ⚠️ CE QUI EST REPRIS À L'IDENTIQUE : tailles, graisses, teintes et géométrie des cellules. Ce qui
// ne l'est PAS, volontairement : la géométrie de la LIGNE (hauteur, rembourrage, survol, en-tête),
// qui appartient au `DataTable` partagé — la maquette la redessine à la main, la production la tient
// d'un seul endroit pour les sept tableaux.
// ══════════════════════════════════════════════════════════════════════════════════════════════

/** Les quatre palettes d'avatar de la maquette, dans son ordre. */
const PALETTES_VENDEUR = [
  ['var(--teal-100)', 'var(--teal-800)'],
  ['var(--blue-bg)', 'var(--blue-fg)'],
  ['var(--violet-bg)', 'var(--violet-fg)'],
  ['var(--amber-bg)', 'var(--amber-fg)'],
] as const;

/**
 * LA COULEUR D'UN VENDEUR — stable, dérivée de son nom.
 *
 * ⚠️ LA MAQUETTE NOMME SES QUATRE VENDEURS EN DUR ; la production en a autant que le magasin. La
 * couleur est donc DÉRIVÉE, et le seul point qui compte est qu'elle soit **stable** : un vendeur qui
 * change de teinte d'une page à l'autre ne se repère plus, et la couleur cesse d'être une information.
 * ⚠️ Ce n'est PAS un identifiant : deux vendeurs peuvent partager une teinte. C'est le nom, à côté,
 * qui identifie — la pastille ne fait qu'aider l'œil à suivre une colonne.
 */
function paletteVendeur(nom: string): readonly [string, string] {
  let h = 0;
  for (let i = 0; i < nom.length; i += 1) h = (h * 31 + nom.charCodeAt(i)) % 9973;
  return PALETTES_VENDEUR[h % PALETTES_VENDEUR.length] as unknown as readonly [string, string];
}

/**
 * Date et heure — la date porte, l'heure accompagne.
 * ⚠️ « FISCAL » SEULEMENT QUAND IL L'EST : l'heure d'un panier abandonné n'est celle d'aucune pièce scellée.
 */
function Horodatage({ valeur, fiscal = true }: { valeur: string; fiscal?: boolean }) {
  const rendu = horodatageFiscal(valeur);
  const coupe = rendu.lastIndexOf(' ');
  const day = coupe > 0 ? rendu.slice(0, coupe) : rendu;
  const heure = coupe > 0 ? rendu.slice(coupe + 1) : '';
  return (
    <span title={fiscal ? t('Horodatage fiscal {valeur}', { valeur }) : undefined} style={{ fontSize: '11.5px', whiteSpace: 'nowrap' }}>
      <span style={{ fontWeight: 600, color: 'var(--ink-800)' }}>{day}</span>
      {heure && <span style={{ color: 'var(--ink-500)' }}> {heure}</span>}
    </span>
  );
}

/** Le numéro de caisse — une pastille, pour qu'il ne se lise pas comme un montant. */
function Caisse({ id }: { id: string }) {
  if (!id) return <Absent />;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', height: 20, padding: '0 7px',
      borderRadius: 'var(--r-xs)', background: 'var(--ink-100)', color: 'var(--ink-600)',
      fontFamily: 'var(--font-mono)', fontSize: '10.5px', fontWeight: 700,
    }}>{codeCaisse(id)}</span>
  );
}

/** L'opérateur — initiale colorée puis nom, comme la maquette. */
function Vendeur({ nom }: { nom: string }) {
  if (!nom) return <Absent />;
  const [fond, encre] = paletteVendeur(nom);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
      <span aria-hidden style={{
        width: 22, height: 22, borderRadius: 'var(--r-pill)', background: fond, color: encre,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '10.5px', fontWeight: 800, flexShrink: 0,
      }}>{nom.slice(0, 1).toUpperCase()}</span>
      <span style={{ fontSize: '13px', color: 'var(--ink-800)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {nom}
      </span>
    </span>
  );
}

/**
 * Le client de la pièce — silhouette pleine s'il est identifié, POINTILLÉE sinon.
 * ⚠️ LE POINTILLÉ EST L'INFORMATION : une vente au comptoir n'est pas un manque, et l'écrire en gris
 * pâle sans signe la ferait lire comme une donnée absente.
 */
function ClientCellule({ nom }: { nom: string }) {
  const identifie = Boolean(nom);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
      <span style={{ display: 'flex', flexShrink: 0, color: identifie ? 'var(--teal-700)' : 'var(--ink-300)' }}>
        <Icon name="user" size={13} stroke={2.1} style={identifie ? undefined : { strokeDasharray: '3 2.6' }} />
      </span>
      <span
        title={identifie ? undefined : t('Vente au comptoir, sans client rattaché')}
        style={{ fontSize: '13px', color: identifie ? 'var(--ink-800)' : 'var(--ink-400)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
      >
        {identifie ? nom : t('Non identifié')}
      </span>
    </span>
  );
}

/** La quantité vendue — une pastille, comme la maquette : elle se compte, elle ne s'additionne pas. */
function Articles({ qtyMilli }: { qtyMilli: number }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      minWidth: 23, height: 20, padding: '0 6px', borderRadius: 'var(--r-xs)',
      background: 'var(--ink-100)', color: 'var(--ink-700)', fontSize: '11.5px', fontWeight: 700,
      fontVariantNumeric: 'tabular-nums',
    }}>
      {formatMinor(qtyMilli, { scale: 3, displayScale: qtyMilli % 1000 === 0 ? 0 : 3 })}
    </span>
  );
}

// ══════════════════════════════════════════════════════════════════════════════════════════════
// LES BLOCS DE LA MODALE — portage de la maquette (§25, markup 11197-11267).
// ══════════════════════════════════════════════════════════════════════════════════════════════

/** Un encart de contexte : la contrainte qui explique l'écran, ou l'événement qui change sa lecture. */
function Encart({ tone, icone, children }: { tone: 'neutre' | 'alerte'; icone: IconName; children: ReactNode }) {
  const alerte = tone === 'alerte';
  return (
    <div
      role={alerte ? 'status' : undefined}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 14px',
        borderRadius: 'var(--r-sm)',
        background: alerte ? 'var(--amber-bg)' : 'var(--ink-50)',
        border: `1px solid ${alerte ? 'var(--amber)' : 'var(--ink-200)'}`,
      }}
    >
      <span aria-hidden style={{ display: 'flex', flexShrink: 0, paddingTop: 1, color: alerte ? 'var(--amber-fg)' : 'var(--ink-500)' }}>
        <Icon name={icone} size={16} stroke={2} />
      </span>
      <span style={{ fontSize: '12.5px', color: alerte ? 'var(--ink-800)' : 'var(--ink-700)', lineHeight: 1.6 }}>
        {children}
      </span>
    </div>
  );
}

/** Un fait d'identité de la pièce : son intitulé en capitales, sa valeur en dessous. */
function Fiche({ heading, valeur, pale }: { heading: string; valeur: string; pale?: boolean }) {
  return (
    <div style={{ padding: '11px 13px', borderRadius: 'var(--r-sm)', border: '1px solid var(--ink-200)' }}>
      <span style={{ display: 'block', fontSize: '10.5px', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ink-500)' }}>
        {heading}
      </span>
      <span style={{ display: 'block', fontSize: '13.5px', fontWeight: 600, color: pale ? 'var(--ink-400)' : 'var(--ink-900)', marginTop: 3 }}>
        {valeur}
      </span>
    </div>
  );
}

/** Une carte bordée à en-tête optionnel — le contenant des lignes et de la ventilation. */
function Carte({ heading, children }: { heading?: string; children: ReactNode }) {
  return (
    <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', overflow: 'hidden' }}>
      {heading && (
        <div style={{
          padding: '10px 14px', background: 'var(--ink-50)', borderBottom: '1px solid var(--ink-200)',
          fontSize: '11px', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ink-500)',
        }}>{heading}</div>
      )}
      {children}
    </div>
  );
}

/**
 * LES GROUPES DE CAISSES, en raccourcis — « les caisses du 2ᵉ étage » en un clic (maquette §Magasin).
 *
 * ⚠️ UN GROUPE EST UNE ACTION, PAS UN ÉTAT, et c'est ce qui décide de sa forme. Rendu en liste
 * déroulante, il afficherait « 2ᵉ étage » longtemps après qu'on a retiré une caisse à la main — le
 * contrôle dirait alors une chose et le filtre en ferait une autre. En pastilles, l'actif se DÉDUIT
 * de la sélection courante : retirer une caisse éteint la pastille toute seule.
 *
 * ⚠️ UN GROUPE VIDE NE S'APPLIQUE PAS. Le poser viderait la sélection, ce que le filtre lit comme
 * « toutes les caisses » — l'exact contraire de ce qu'on vient de demander, et sans un mot. La
 * pastille est donc inerte et dit pourquoi.
 */
function GroupesDeCaisses({ groupes, registers, onAppliquer }: {
  groupes: RegisterGroup[];
  registers: string[];
  onAppliquer: (registerIds: string[]) => void;
}) {
  /** Le groupe actif = celui dont les membres sont EXACTEMENT la sélection courante. */
  const memeSelection = (ids: string[]) =>
    ids.length > 0 && ids.length === registers.length && ids.every((i) => registers.includes(i));

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>{t('Groupes')}</span>
      {groupes.map((g) => {
        const actif = memeSelection(g.registerIds);
        const vide = g.registerIds.length === 0;
        return (
          <Tooltip key={g.id}
            content={vide
              ? t('« {name} » ne contient aucune caisse — à compléter dans Réglages → Magasin & caisses.', { name: g.name })
              : actif
                ? t('Cliquer à nouveau pour revenir à toutes les caisses')
                : t('Filtrer sur {v}', { v: g.registerIds.map(codeCaisse).join(', ') })}>
            <button type="button" disabled={vide} aria-pressed={actif}
              onClick={() => onAppliquer(actif ? [] : g.registerIds)}
              style={{
                height: 28, padding: '0 11px', borderRadius: 'var(--r-pill)',
                border: `1.5px solid ${actif ? 'var(--teal-500)' : 'var(--ink-200)'}`,
                background: actif ? 'var(--teal-100)' : 'var(--paper)',
                color: vide ? 'var(--ink-400)' : actif ? 'var(--teal-800)' : 'var(--ink-600)',
                fontSize: 'var(--t-meta)', fontWeight: 700, whiteSpace: 'nowrap',
                cursor: vide ? 'not-allowed' : 'pointer',
              }}>{g.name}</button>
          </Tooltip>
        );
      })}
    </span>
  );
}
